/** «К перевозу» на странице ФБС — ЧИСТАЯ модель правок менеджера.
 *
 *  Расчёт даёт `recommended` (сколько довезти по горизонту, после округления до короба и среза
 *  остатком источника), но уезжает в заказ на перемещение РОВНО то, что человек оставил в колонке
 *  «К перевозу». Состояние правок — `{nom_key: qty}`: в нём лежат ТОЛЬКО тронутые строки, у
 *  остальных количество равно рекомендации. Так «Пересчитать» с новыми параметрами меняет числа
 *  нетронутых строк, а введённое руками не затирает.
 *
 *  Без молчаливых фоллбэков: неразобранный ввод НЕ превращается ни в ноль, ни в рекомендацию —
 *  `setQty` возвращает прежнее состояние и текст причины, который страница показывает у поля.
 */

/** Правки человека: номенклатура → количество к перевозу. Только тронутые строки. */
export type FbsEdits = Readonly<Record<string, number>>;

/** Ничего не правили. Общая константа — чтобы «пусто» везде было одним значением. */
export const NO_EDITS: FbsEdits = {};

/** Строка отчёта в том объёме, который нужен модели (полный вид — `api.FbsReportRow`). */
export interface FbsEditableRow {
  nom_key: string;
  recommended: number;
}

/** Сколько везти по строке: правка человека, иначе рекомендация расчёта. */
export function initialQty(row: FbsEditableRow, edits: FbsEdits): number {
  const edited = edits[row.nom_key];
  return edited === undefined ? row.recommended : edited;
}

/** Результат правки поля: новое состояние либо причина отказа (состояние тогда прежнее). */
export interface SetQtyResult {
  edits: FbsEdits;
  error: string | null;
}

/** Правка количества по строке. Пустое поле = «не везём» (0) — это осмысленный ответ, а не
 *  «ввод не разобрали». Всё остальное, что не целое ≥ 0, отвергается с текстом.
 *
 *  `maxQty` — потолок (свободный ГП источника) для строк, где количество целиком вводит человек
 *  («Без продаж ФБС»): у расчётных строк рекомендация уже срезана ГП, а здесь срезать некому. */
export function setQty(
  edits: FbsEdits,
  nomKey: string,
  raw: string,
  maxQty?: number,
): SetQtyResult {
  const text = raw.trim();
  if (text === "") return { edits: { ...edits, [nomKey]: 0 }, error: null };
  if (!/^\d+$/.test(text))
    return { edits, error: "только целое число штук, без знаков и дробей" };
  const qty = Number(text);
  if (!Number.isSafeInteger(qty)) return { edits, error: "слишком большое число" };
  if (maxQty !== undefined && qty > maxQty)
    return { edits, error: `больше свободного остатка ГП (${Math.max(0, Math.floor(maxQty))} шт)` };
  return { edits: { ...edits, [nomKey]: qty }, error: null };
}

/** «Обнулить к перемещению»: ноль во ВСЕХ строках (расчётных и «Без продаж ФБС») — дальше человек
 *  проставляет руками. Вернуть расчёт целиком — `NO_EDITS`, по строке — `clearQty`. */
export function zeroAll(nomKeys: readonly string[]): FbsEdits {
  return Object.fromEntries(nomKeys.map((k) => [k, 0]));
}

/** Снять правку строки — вернуть её к рекомендации расчёта. */
export function clearQty(edits: FbsEdits, nomKey: string): FbsEdits {
  if (!(nomKey in edits)) return edits;
  const next = { ...edits };
  delete next[nomKey];
  return next;
}

/** Сколько штук уедет всего (по ВСЕМ строкам отчёта, не только по правленым). */
export function totalQty(rows: readonly FbsEditableRow[], edits: FbsEdits): number {
  return rows.reduce((sum, row) => sum + initialQty(row, edits), 0);
}

/** Строка будущего документа. */
export interface FbsLine {
  nom_key: string;
  qty: number;
}

/** Что уедет в заказ на перемещение: только строки с положительным количеством. Порядок —
 *  как в отчёте (он отсортирован по потребности), чтобы документ читался сверху вниз так же. */
export function linesToSend(rows: readonly FbsEditableRow[], edits: FbsEdits): FbsLine[] {
  const lines: FbsLine[] = [];
  for (const row of rows) {
    const qty = initialQty(row, edits);
    if (qty > 0) lines.push({ nom_key: row.nom_key, qty });
  }
  return lines;
}
